import { z } from "zod";

// Unified "attention" read-model. An *occurrence* is a
// single thing demanding operator/gestor attention: an alarm and the
// pending stop justification of the SAME occurrence (linked via machineEvent.alarmId
// ↔ alarm) are merged into ONE occurrence carrying BOTH attributes, counted once.
// Alarm and MachineEvent stay distinct persisted entities — merged only on read.

// Sub-attributes that can coexist on one occurrence.
export const AttentionAlarmRefSchema = z.object({
  refId: z.string().uuid(), // alarm.id
  state: z.enum(["UNACKNOWLEDGED", "ACKNOWLEDGED", "RESOLVED", "SHELVED"]),
  title: z.string(),
  severity: z.enum(["HIGH", "MEDIUM", "LOW"]),
  // Confiabilidade do alarme (Princípio IV). SUSPECT quando oscilando.
  reliability: z.enum(["HIGH", "MEDIUM", "LOW", "SUSPECT"]),
  flapping: z.boolean(), // alarme oscilando (anti-flood ISA 18.2) — sinal de threshold/sensor instável
});

export const AttentionJustificationRefSchema = z.object({
  refId: z.string().uuid(), // machineEvent.id
  type: z.enum(["STOP", "NODATA"]),
  reasonStatus: z.enum(["PENDING", "JUSTIFIED"]),
  acknowledgedAt: z.string().datetime({ offset: true }).nullable(),
});

// Canonical status semantics:
// - ACTIVE                = ocorrência tem alarme ativo (não resolvido). Badge "Ativo";
//                           sub-estado reconhecido/não-reconhecido vem de alarm.state.
// - PENDING_JUSTIFICATION = ocorrência tem evento sem causa registrada. Badge "Justificativa Pendente".
// São atributos independentes que podem coexistir (não mutuamente exclusivos).
export const AttentionStatusSchema = z.enum(["ACTIVE", "PENDING_JUSTIFICATION"]);

export const AttentionOccurrenceSchema = z.object({
  occurrenceId: z.string().uuid(), // event.id se houver evento; senão alarm.id
  machineId: z.string().uuid(),
  machineCode: z.string(),
  lineId: z.string().uuid(),
  lineName: z.string(),
  since: z.string().datetime({ offset: true }), // menor startAt entre alarme/evento
  reason: z.string().nullable(), // motivo associado (040)
  action: z.string(),
  // Severidade graduada da ocorrência. Alarme → alarm.severity;
  // ocorrência sem alarme (NODATA) → derivada do tipo de evento.
  severity: z.enum(["HIGH", "MEDIUM", "LOW"]),
  alarm: AttentionAlarmRefSchema.nullable(),
  justification: AttentionJustificationRefSchema.nullable(),
  // Pendentes têm ≥1 status; resolvidos (scope=resolved) podem ter statuses vazio
  // (alarme RESOLVED não é ACTIVE; evento JUSTIFIED não é PENDING_JUSTIFICATION).
  statuses: z.array(AttentionStatusSchema),
  resolvedAt: z.string().datetime({ offset: true }).nullable(), // só relevante em scope=resolved
});
export type AttentionOccurrence = z.infer<typeof AttentionOccurrenceSchema>;

// Per-line response (GET /lines/:lineId/attention). Pendentes.
export const AttentionResponseSchema = z.object({
  items: z.array(AttentionOccurrenceSchema),
  summary: z.object({
    pendingJustifications: z.number().int().nonnegative(),
    activeAlarms: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
    // alarmes suprimidos por anti-flood (não viram itens, mas a contagem permanece visível)
    suppressedAlarms: z.number().int().nonnegative(),
  }),
});
export type AttentionResponse = z.infer<typeof AttentionResponseSchema>;

// Org-wide query/response (GET /attention) — usado pela /alarms consolidada.
export const OrgAttentionQuerySchema = z.object({
  scope: z.enum(["pending", "resolved"]).default("pending"),
  lineId: z.string().uuid().optional(),
  type: z.enum(["ALARM", "STOP", "NODATA"]).optional(),
  // Filtros de triagem. `flapping` é querystring: "true"/"false"
  // explícitos (NÃO `z.coerce.boolean`, que faz Boolean("false") === true e inverteria o filtro).
  severity: z.enum(["HIGH", "MEDIUM", "LOW"]).optional(),
  flapping: z
    .enum(["true", "false"])
    .transform((v) => v === "true")
    .optional(), // true → apenas ocorrências oscilando
  limit: z.coerce.number().int().positive().max(500).default(100),
  // Paginação server-side (convenção offset/limit da feature audit).
  offset: z.coerce.number().int().min(0).default(0),
});
export type OrgAttentionQuery = z.infer<typeof OrgAttentionQuerySchema>;

// Paginação real (substitui o flag `truncated`): metadados dentro de `data`
// (o api-client descarta `meta`). `total` = conjunto filtrado completo → permite alcançar o último
// registro sem truncamento. A ordenação da ocorrência é severidade/instabilidade primeiro (não
// cronológica), então NÃO dá para limitar o fetch por janela de recência sem perder ocorrências
// antigas de alta prioridade (code-review): o conjunto é materializado e fatiado. Custo de memória
// em históricos enormes é limitação conhecida; follow-up = keyset por (severidade, since).
export const OrgAttentionResponseSchema = z.object({
  items: z.array(AttentionOccurrenceSchema),
  total: z.number().int().nonnegative(),
  offset: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
});
export type OrgAttentionResponse = z.infer<typeof OrgAttentionResponseSchema>;

export const AttentionEnvelopeSchema = z.object({ data: AttentionResponseSchema, error: z.null() });
export const OrgAttentionEnvelopeSchema = z.object({
  data: OrgAttentionResponseSchema,
  error: z.null(),
});
